import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowRightIcon, CalendarIcon, HandIcon, HeartIcon, MapPinIcon, SparklesIcon, UsersIcon } from 'lucide-react';
import { LOCATIONS, OPENING_HOURS } from '../data/locations';
import { LocationCard } from '../components/LocationCard';
import { PricingMenus } from '../components/PricingMenus';
import { SectionHeading } from '../components/SectionHeading';
import { TherapistCard } from '../components/TherapistCard';
import { THERAPISTS } from '../data/therapists';
const HERO_IMAGE = 'https://cdn.magicpatterns.com/patterns/generated-images/11ae6ca3-d026-4d2e-a01d-91562f27ed20.jpg';
const STATS = [{
  icon: MapPinIcon,
  value: '9',
  label: 'Locations'
}, {
  icon: UsersIcon,
  value: '20+',
  label: 'Trusted Therapists'
}, {
  icon: CalendarIcon,
  value: '300+',
  label: 'Weekly Bookings'
}];
const FEATURES = [{
  icon: HandIcon,
  title: 'Skilled hands',
  body: 'Our therapists draw on traditional techniques from across East and Southeast Asia, refined over years of practice.'
}, {
  icon: HeartIcon,
  title: 'Private & discreet',
  body: 'Low light, soft music, a room to yourself. Every visit stays between you and your therapist.'
}, {
  icon: SparklesIcon,
  title: 'Open late, seven days',
  body: `${OPENING_HOURS}. Walk in or book ahead — whatever suits your evening.`
}];
export function Home() {
  return <main className="w-full bg-ink-950" data-id="element-230" data-mp="2i00c2ora4ejx">
      <section className="relative w-full overflow-hidden" data-id="element-231" data-mp="2d6p0697m17l5">
        <img src={HERO_IMAGE} alt="" aria-hidden="true" className="absolute inset-0 h-full w-full object-cover" data-id="element-232" data-mp="2d5494tki86s1" />
        <div className="absolute inset-0 bg-gradient-to-b from-ink-950/85 via-ink-950/45 to-ink-950" data-id="element-233" data-mp="23ac7nenswzrz" />
        <div className="absolute inset-0 mix-blend-soft-light" style={{
        backgroundImage: 'radial-gradient(60% 55% at 12% 15%, rgba(232,54,127,0.55) 0%, rgba(232,54,127,0) 65%), radial-gradient(60% 55% at 88% 25%, rgba(168,85,247,0.5) 0%, rgba(168,85,247,0) 65%)'
      }} data-id="element-234" data-mp="1z64jbil99lua" />
        <motion.div initial={{
        opacity: 0,
        y: 24
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.8,
        ease: [0.22, 1, 0.36, 1]
      }} className="relative mx-auto flex w-full max-w-4xl flex-col items-center px-5 py-32 text-center sm:px-8 sm:py-44" data-id="element-235" data-mp="1ojzfsgzuc68g">
          <span className="rounded-full border border-lotus-500/50 bg-lotus-500/10 px-5 py-2 text-[11px] font-medium uppercase tracking-[0.32em] text-lotus-300 backdrop-blur" data-id="element-236" data-mp="23dkt7jw6mw6k">
            Men only · 18+
          </span>

          <h1 className="text-shadow-soft mt-10 font-display text-5xl font-light leading-[1.05] tracking-wide text-white sm:text-7xl" data-id="element-237" data-mp="1yk8hubgiklvr">
            Slow down.
            <br data-id="element-238" data-mp="2m8rpcorqoums" />
            <em className="text-neon italic" data-id="element-239" data-mp="2m89iyzbd5015">This hour is yours.</em>
          </h1>

          <div className="rule-gold mt-8 w-32" data-id="element-240" data-mp="2r2vwp94x34oe" />

          <p className="mt-8 max-w-xl text-lg font-light leading-relaxed text-neutral-300" data-id="element-241" data-mp="fl0srvibr2wi">
            Nine private locations across Brisbane and the Gold Coast. Warm oil, dim light and
            therapists who know exactly where the tension sits.
          </p>

          <div className="mt-12 flex flex-col gap-4 sm:flex-row" data-id="element-242" data-mp="ofyrjj85l9vr">
            <Link to="/book" className="glow-pink group inline-flex items-center justify-center gap-2.5 rounded-full bg-lotus-500 px-10 py-4 text-sm font-semibold uppercase tracking-[0.18em] text-white transition-colors hover:bg-lotus-600 focus:outline-none focus-visible:ring-2 focus-visible:ring-lotus-400 focus-visible:ring-offset-2 focus-visible:ring-offset-ink-950" data-id="element-243" data-mp="kd2c21dwttw1">
              Book Now
              <ArrowRightIcon className="h-4 w-4 transition-transform group-hover:translate-x-1" aria-hidden="true" data-id="element-244" data-mp="1h2t47tyqb4yo" />
            </Link>
            <Link to="/locations" className="inline-flex items-center justify-center rounded-full border border-orchid-400/50 px-10 py-4 text-sm font-semibold uppercase tracking-[0.18em] text-orchid-300 backdrop-blur transition-colors hover:border-orchid-400 hover:bg-orchid-500/15 hover:text-white focus:outline-none focus-visible:ring-2 focus-visible:ring-orchid-400 focus-visible:ring-offset-2 focus-visible:ring-offset-ink-950" data-id="element-245" data-mp="1c5t2twctqqdw">
              Find a Location
            </Link>
          </div>
        </motion.div>
      </section>

      <section aria-label="Key figures" className="w-full border-y border-lotus-500/20 bg-ink-900" data-id="element-246" data-mp="2va9aftgpx2oo">
        <div className="mx-auto grid w-full max-w-5xl divide-y divide-white/5 px-5 sm:px-8 md:grid-cols-3 md:divide-x md:divide-y-0" data-id="element-247" data-mp="304n862iurlwi">
          {STATS.map((stat, i) => <motion.div key={stat.label} initial={{
          opacity: 0,
          y: 14
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true,
          margin: '-60px'
        }} transition={{
          duration: 0.45,
          delay: i * 0.08,
          ease: 'easeOut'
        }} className="flex flex-col items-center py-12 text-center" data-id="element-248" data-mp="3a2b611br99iw" data-mpi={i}>
              <stat.icon className="h-5 w-5 text-orchid-400" aria-hidden="true" data-id="element-249" data-mp="tdid4c3wa542" />
              <span className="text-neon mt-4 font-display text-5xl font-light" data-id="element-250" data-mp="ofgv7ui7gbu6">{stat.value}</span>
              <p className="mt-3 text-[11px] font-medium uppercase tracking-[0.28em] text-neutral-400" data-id="element-251" data-mp="1m2phvstxzqxr">
                {stat.label}
              </p>
            </motion.div>)}
        </div>
      </section>

      <section aria-labelledby="why-heading" className="glow-warm w-full bg-ink-950" data-id="element-252" data-mp="1cce3fccmtzcm">
        <div className="mx-auto w-full max-w-7xl px-5 py-24 sm:px-8" data-id="element-253" data-mp="17el14ueg9phm">
          <SectionHeading id="why-heading" eyebrow="The Experience" title="Why Me Time" accent="Me Time" subtitle="A consistent, unhurried experience at every location across Brisbane and the Gold Coast." data-id="element-254" data-mp="17c8i0dwyh92z" />
          <div className="mt-16 grid gap-6 md:grid-cols-3" data-id="element-255" data-mp="3ubdw7ktbjvy9">
            {FEATURES.map((feature, i) => <motion.article key={feature.title} initial={{
            opacity: 0,
            y: 18
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true,
            margin: '-60px'
          }} transition={{
            duration: 0.45,
            delay: i * 0.08,
            ease: 'easeOut'
          }} className="group rounded-2xl border border-white/[0.07] bg-ink-850/70 p-8 transition-all duration-300 hover:border-lotus-500/50 hover:bg-ink-800/70" data-id="element-256" data-mp="28w7jrm4uf1b" data-mpi={i}>
                <span className="flex h-12 w-12 items-center justify-center rounded-full border border-orchid-400/40 bg-orchid-500/10 text-orchid-300 transition-colors group-hover:border-lotus-500/60 group-hover:bg-lotus-500/15 group-hover:text-lotus-300" data-id="element-257" data-mp="31nrihjqdnaxo">
                  <feature.icon className="h-5 w-5" aria-hidden="true" data-id="element-258" data-mp="14twr6yddg6fy" />
                </span>
                <h3 className="mt-6 font-display text-2xl font-light tracking-wide text-white" data-id="element-259" data-mp="1ex239rxeofsn">
                  {feature.title}
                </h3>
                <p className="mt-3 text-sm font-light leading-relaxed text-neutral-400" data-id="element-260" data-mp="1a0bkgwu7v63j">
                  {feature.body}
                </p>
              </motion.article>)}
          </div>
        </div>
      </section>

      <section aria-labelledby="ladies-heading" className="glow-warm w-full border-y border-orchid-500/20 bg-ink-900" data-id="element-261" data-mp="1xo31ueum69bo">
        <div className="mx-auto w-full max-w-7xl px-5 py-24 sm:px-8" data-id="element-262" data-mp="1so6t62az1tps">
          <SectionHeading id="ladies-heading" eyebrow="The Team" title="Our Ladies" accent="Ladies" subtitle="Experienced therapists from across East and Southeast Asia — each with her own touch. Ask for anyone by name." data-id="element-263" data-mp="26p9h6klj1e3c" />
          <div className="mt-16 grid gap-6 sm:grid-cols-2 lg:grid-cols-3" data-id="element-264" data-mp="89epuepxqte6">
            {THERAPISTS.slice(0, 3).map((therapist, i) => <TherapistCard key={therapist.slug} therapist={therapist} index={i} data-id="element-265" data-mp="wta202gg0kuh" data-mpi={i} />)}
          </div>
          <div className="mt-12 text-center" data-id="element-266" data-mp="11rbo6w9610a6">
            <Link to="/therapists" className="group inline-flex items-center gap-2.5 rounded-full border border-lotus-500/50 px-9 py-3.5 text-[11px] font-semibold uppercase tracking-[0.2em] text-lotus-300 transition-colors hover:bg-lotus-500 hover:text-white" data-id="element-267" data-mp="16jcve3rh80tc">
              Meet all our ladies
              <ArrowRightIcon className="h-3.5 w-3.5 transition-transform group-hover:translate-x-1" aria-hidden="true" data-id="element-268" data-mp="iv3hrp4f2jda" />
            </Link>
          </div>
        </div>
      </section>

      <section aria-labelledby="pricing-heading" className="w-full bg-ink-950" data-id="element-269" data-mp="rsvxuvhypv1q">
        <div className="mx-auto w-full max-w-5xl px-5 py-24 sm:px-8" data-id="element-270" data-mp="1yasrcoexazw8">
          <PricingMenus data-id="element-271" data-mp="23941z1d1lefi" />
        </div>
      </section>

      <section aria-labelledby="locations-heading" className="glow-warm w-full bg-ink-900" data-id="element-272" data-mp="23byjak0rej4p">
        <div className="mx-auto w-full max-w-7xl px-5 py-24 sm:px-8" data-id="element-273" data-mp="2dfwwnz0zgps5">
          <SectionHeading id="locations-heading" eyebrow="Our Locations" title="Find your location" accent="your location" subtitle="Nine venues across Brisbane and the Gold Coast, open seven days until late." data-id="element-274" data-mp="28fiwt49jcm57" />
          <div className="mt-16 grid gap-6 sm:grid-cols-2 lg:grid-cols-3" data-id="element-275" data-mp="1uomafv67d16l">
            {LOCATIONS.map((location, i) => <LocationCard key={location.slug} location={location} index={i} data-id="element-276" data-mp="1pn6kbop644hc" data-mpi={i} />)}
          </div>
        </div>
      </section>

      <section className="relative w-full overflow-hidden bg-ink-950" data-id="element-277" data-mp="1go30ujm39n2b">
        <div className="glow-warm absolute inset-0" data-id="element-278" data-mp="1gpwc6c7ncvgn" />
        <div className="relative mx-auto w-full max-w-3xl px-5 py-28 text-center sm:px-8" data-id="element-279" data-mp="3f2eglc6yykw1">
          <h2 className="font-display text-4xl font-light tracking-wide text-white sm:text-5xl" data-id="element-280" data-mp="3b1krifhsj01k">
            Ready to <em className="text-neon italic" data-id="element-281" data-mp="cnjvqd180kxk">unwind</em>?
          </h2>
          <div className="rule-gold mx-auto mt-6 w-28" data-id="element-282" data-mp="8n9q51hcbwj8" />
          <p className="mt-6 font-light text-neutral-300" data-id="element-283" data-mp="8mq7tjns364o">
            Choose your location and book a session in under a minute.
          </p>
          <Link to="/book" className="glow-pink group mt-10 inline-flex items-center justify-center gap-2.5 rounded-full bg-lotus-500 px-10 py-4 text-sm font-semibold uppercase tracking-[0.18em] text-white transition-colors hover:bg-lotus-600" data-id="element-284" data-mp="hnxvz8wkyql1">
            Book Now
            <ArrowRightIcon className="h-4 w-4 transition-transform group-hover:translate-x-1" aria-hidden="true" data-id="element-285" data-mp="3nn545v9tbzv" />
          </Link>
        </div>
      </section>
    </main>;
}